// ════════════════════════════════════════════════════════════
// /paiements/$id — vue détail + actions par rôle (version light ECR)
// ════════════════════════════════════════════════════════════
// - Header : code + status + mode
// - Stepper Créé → Confirmé Sage
// - Resume business (client, mode-specific, banques, wallet, montant)
// - Thumbnails justificatifs (photo, bordereau)
// - Anomalies inline (affichage + acquittement)
// - Historique timeline (qui/quand)
// - Sticky actions bar mobile (confirm Sage / reject / mark argent recu /
//   upload bordereau / annuler / delete)
// ════════════════════════════════════════════════════════════
import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Wallet,
  Banknote,
  FileText,
  ScrollText,
  ArrowRightLeft,
  ClipboardCheck,
  Send,
  MessageCircle,
  Trash2,
  Share2,
  Image as ImageIcon,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  usePaiement,
  usePaiementAnomalies,
  useConfirmPaiement,
  useRejectPaiement,
  useMarkArgentRecu,
  useAnnulerPaiement,
  useDeletePaiement,
  useAcknowledgePaiementAnomaly,
  useBanques,
  useWalletsProviders,
  useUploadBordereauPaiement,
  useUpdatePaiement,
  isPaiementMemeBanque,
} from '@/queries/paiements';
import { useComptesTresorerie } from '@/queries/tresorerie';
import { getPaiementSignedUrl } from '@/lib/uploadStorage';
import { shareOrDownloadFile, cleanShareFilename } from '@/lib/shareFile';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Label } from '@/components/ui/Label';
import { cn } from '@/lib/utils';
import {
  PAIEMENT_STEPS,
  STATUS_LABELS,
  STATUS_COLORS,
  SEVERITY_COLORS,
  AUTO_CONFIRM_REASON_LABELS,
  computeActions,
  stepIndex,
  PaiementConfirmSchema,
  PaiementRejectSchema,
  type Paiement,
  type PaiementAnomaly,
  type PaiementMode,
} from '@ecr/core/paiements';
import { formatMRU } from '@ecr/core/utils';

export const Route = createFileRoute('/_authed/paiements/$id')({
  component: PaiementDetailPage,
});

const MODE_LABELS: Record<PaiementMode, string> = {
  cash: 'Cash',
  cheque: 'Chèque',
  wallet: 'Wallet',
  traite: 'Traite',
  virement: 'Virement',
};

const MODE_ICONS: Record<PaiementMode, typeof Banknote> = {
  cash: Banknote,
  cheque: FileText,
  wallet: Wallet,
  traite: ScrollText,
  virement: ArrowRightLeft,
};

const ANOMALY_TYPE_LABELS: Record<string, string> = {
  doublon: 'Doublon',
  num_cheque_deja_vu: 'N° chèque déjà vu',
  capture_wallet_suspecte: 'Capture wallet suspecte',
  expediteur_wallet_mismatch: 'Expéditeur ≠ client',
  rattrapage_tardif: 'Rattrapage tardif',
  traite_reports_excessifs: 'Traite reportée trop souvent',
};

function PaiementDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { session, profile } = useAuth();
  const userEmail = session?.user?.email ?? '';
  const role = profile?.role ?? null;

  const { data: paiement, isLoading, error } = usePaiement(id);
  const { data: anomalies = [] } = usePaiementAnomalies({
    paiementId: id,
    includeAcknowledged: true,
  });
  const { data: banques = [] } = useBanques();
  const { data: wallets = [] } = useWalletsProviders();
  const { data: comptes = [] } = useComptesTresorerie();

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="bg-muted h-8 w-1/3 animate-pulse rounded" />
        <div className="bg-muted h-24 animate-pulse rounded" />
        <div className="bg-muted h-40 animate-pulse rounded" />
      </div>
    );
  }
  if (error || !paiement) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 py-6">
          <AlertCircle className="size-5 text-red-600" />
          <p className="text-sm">
            {error instanceof Error ? `Erreur : ${error.message}` : 'Encaissement introuvable.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const compteEnc = comptes.find((c) => c.id === paiement.banqueEncaissementId);
  // Cash en caisse (Caisse Mauripe) : pas de dépôt bancaire ni bordereau,
  // la caisse se vide par transferts trésorerie (décision Sidi 2026-08-27).
  const cashEnCaisse = paiement.mode === 'cash' && compteEnc?.type === 'caisse';

  const actions = computeActions({
    status: paiement.status,
    mode: paiement.mode,
    argentRecu: paiement.argentRecu,
    traiteEncaisseAt: paiement.traiteEncaisseAt,
    role,
    createdByEmail: paiement.createdByEmail,
    currentUserEmail: userEmail,
    bordereauUrl: paiement.bordereauUrl,
    banqueEmettriceId: paiement.banqueEmettriceId,
    banqueEncaissementId: paiement.banqueEncaissementId,
    compteEncaissementType: compteEnc?.type ?? null,
  });

  const stepIdx = stepIndex(paiement.status);
  const ModeIcon = MODE_ICONS[paiement.mode];
  const banqueEm = banques.find((b) => b.id === paiement.banqueEmettriceId);
  const wallet = wallets.find((w) => w.id === paiement.walletProviderId);
  // Règle « même banque » : réseau émettrice (banques.code) = réseau du compte.
  const memeBanque = isPaiementMemeBanque(paiement, banques, comptes);

  // Annulation générique (light ECR) : DAF only, tant que pas déjà annulé/rejeté.
  const canAnnuler =
    role === 'daf' && (paiement.status === 'en_attente' || paiement.status === 'confirme');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/paiements">
          <Button variant="ghost" size="icon" aria-label="Retour">
            <ArrowLeft className="size-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-foreground font-mono text-xl font-semibold sm:text-2xl">
              {paiement.code}
            </h1>
            <span
              className="rounded-full px-2.5 py-0.5 text-xs font-medium"
              style={{
                color: STATUS_COLORS[paiement.status].fg,
                backgroundColor: STATUS_COLORS[paiement.status].bg,
              }}
            >
              {STATUS_LABELS[paiement.status]}
            </span>
            <span className="text-muted-foreground inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium dark:bg-slate-800">
              <ModeIcon className="size-3" />
              {MODE_LABELS[paiement.mode]}
            </span>
            {paiement.argentRecuAuto && paiement.argentRecuAutoReason && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950/50 dark:text-blue-300">
                Auto · {AUTO_CONFIRM_REASON_LABELS[paiement.argentRecuAutoReason]}
              </span>
            )}
          </div>
          <p className="text-muted-foreground mt-1 text-sm">
            {new Intl.DateTimeFormat('fr-FR', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }).format(paiement.createdAt)}{' '}
            · par {paiement.createdByEmail.split('@')[0]}
          </p>
        </div>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(
            `Encaissement ${paiement.code} · ${paiement.clientNom} · ${formatMRU(
              paiement.montant,
            )} · ${STATUS_LABELS[paiement.status]}`,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-foreground hidden shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs sm:inline-flex"
        >
          <MessageCircle className="size-3.5" />
          WhatsApp
        </a>
      </div>

      {/* Bandeau "À déposer" : chèque, ou cash versé sur un compte bancaire.
          Cash en caisse (Caisse Mauripe) : rien à déposer, pas de bandeau. */}
      {(paiement.mode === 'cheque' || (paiement.mode === 'cash' && !cashEnCaisse)) &&
        !paiement.bordereauUrl &&
        paiement.status === 'en_attente' && (
          <ChequeADeposerBanner
            paiement={paiement}
            canUpload={actions.canUploadBordereau}
            memeBanque={memeBanque}
          />
        )}

      {/* Sprint 4 patch : bandeau "Rejeté par comptable" — visible pour Fatimetou (V1 fidèle) */}
      {paiement.status === 'rejete' && paiement.rejetMotif && (
        <PaiementRejeteBanner paiement={paiement} canEdit={actions.canEdit} />
      )}

      {/* Stepper */}
      {paiement.status !== 'rejete' && paiement.status !== 'annule' && stepIdx >= 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="relative flex items-center justify-between">
              {PAIEMENT_STEPS.map((step, i) => {
                const reached = i <= stepIdx;
                const current = i === stepIdx;
                return (
                  <div key={step} className="relative z-10 flex flex-col items-center gap-1">
                    <div
                      className={cn(
                        'flex size-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-all',
                        reached
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-muted-foreground',
                        current && 'ring-primary/30 scale-110 ring-4',
                      )}
                    >
                      {i + 1}
                    </div>
                    <p
                      className={cn(
                        'text-xs',
                        reached ? 'text-foreground font-medium' : 'text-muted-foreground',
                      )}
                    >
                      {STATUS_LABELS[step]}
                    </p>
                  </div>
                );
              })}
              <div className="bg-border absolute left-4 right-4 top-4 -z-0 h-0.5">
                <div
                  className="bg-primary h-full transition-all"
                  style={{
                    width: `${(stepIdx / Math.max(PAIEMENT_STEPS.length - 1, 1)) * 100}%`,
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resume */}
      <Card>
        <CardContent className="space-y-3 py-4 sm:py-5">
          <div className="flex items-baseline justify-between">
            <p className="text-muted-foreground text-sm">Montant</p>
            <p className="text-foreground font-mono text-2xl font-semibold tabular-nums">
              {formatMRU(paiement.montant)}
            </p>
          </div>
          <div className="border-border space-y-1 border-t pt-3">
            <DetailRow label="Client" value={paiement.clientNom} />
            {paiement.numCheque && <DetailRow label="N° chèque" value={paiement.numCheque} mono />}
            {banqueEm && <DetailRow label="Banque émettrice" value={banqueEm.label} />}
            {compteEnc && <DetailRow label="Compte d'encaissement" value={compteEnc.label} />}
            {wallet && <DetailRow label="Wallet" value={wallet.label} />}
            {paiement.refTransaction && (
              <DetailRow label="Référence" value={paiement.refTransaction} mono />
            )}
            {paiement.dateCheque && (
              <DetailRow
                label="Date chèque"
                value={new Intl.DateTimeFormat('fr-FR').format(paiement.dateCheque)}
              />
            )}
            {paiement.dateTransaction && (
              <DetailRow
                label="Date transaction"
                value={new Intl.DateTimeFormat('fr-FR', {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                }).format(paiement.dateTransaction)}
              />
            )}
            {paiement.dateEncaissement && (
              <DetailRow
                label="Date encaissement"
                value={new Intl.DateTimeFormat('fr-FR').format(paiement.dateEncaissement)}
              />
            )}
            {paiement.isRattrapage && (
              <DetailRow
                label="Rattrapage"
                value={
                  <span className="text-orange-700 dark:text-orange-300">
                    {paiement.rattrapageJours} jours
                  </span>
                }
              />
            )}
            {paiement.refCompta && <DetailRow label="Réf Sage" value={paiement.refCompta} mono />}
            {paiement.commentaire && <DetailRow label="Commentaire" value={paiement.commentaire} />}
            {paiement.rejetMotif && (
              <DetailRow
                label="Motif rejet"
                value={
                  <span className="text-red-700 dark:text-red-300">{paiement.rejetMotif}</span>
                }
              />
            )}
            {paiement.annuleeMotif && (
              <DetailRow
                label="Motif annulation"
                value={
                  <span className="text-red-700 dark:text-red-300">{paiement.annuleeMotif}</span>
                }
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Justificatifs */}
      {(paiement.photoUrl ?? paiement.bordereauUrl) != null && (
        <Card>
          <CardContent className="space-y-3 py-4">
            <p className="text-foreground font-medium">Justificatifs</p>
            <div className="flex flex-wrap gap-2">
              {paiement.photoUrl && (
                <JustifThumb path={paiement.photoUrl} kind="Photo" paiementCode={paiement.code} />
              )}
              {paiement.bordereauUrl && (
                <JustifThumb
                  path={paiement.bordereauUrl}
                  kind="Bordereau"
                  paiementCode={paiement.code}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <Card className="border-orange-200 dark:border-orange-900">
          <CardContent className="space-y-2 py-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="size-4 text-orange-600 dark:text-orange-400" />
              <p className="font-medium text-orange-900 dark:text-orange-100">
                Anomalies détectées ({anomalies.length})
              </p>
            </div>
            <div className="space-y-2">
              {anomalies.map((a) => (
                <AnomalyInline key={a.id} anomaly={a} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique */}
      <HistoryTimeline paiement={paiement} />

      {/* Actions bar (sticky bottom mobile) */}
      <ActionsBar
        paiement={paiement}
        actions={actions}
        canAnnuler={canAnnuler}
        memeBanque={memeBanque}
        onDeleted={() => {
          void navigate({ to: '/paiements' });
        }}
      />
    </div>
  );
}

// ─── DetailRow ───────────────────────────────────────────
function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 text-sm">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={cn('text-foreground text-right', mono && 'font-mono')}>{value}</span>
    </div>
  );
}

// ─── JustifThumb ─────────────────────────────────────────
function JustifThumb({
  path,
  kind,
  paiementCode,
}: {
  path: string;
  kind: string;
  paiementCode: string;
}) {
  const { data: signedUrl, isLoading } = useQuery({
    queryKey: ['paiement-signed-url', path],
    queryFn: () => getPaiementSignedUrl(path),
    staleTime: 30 * 60 * 1000,
  });
  const [sharing, setSharing] = useState(false);
  const isImage = /\.(jpe?g|png|webp|gif)(\?|$)/i.test(path);
  const filename = path.split('/').pop() ?? 'fichier';

  async function handleShare() {
    if (!signedUrl) return;
    setSharing(true);
    try {
      const outcome = await shareOrDownloadFile({
        signedUrl,
        filename: cleanShareFilename(kind, paiementCode, path),
        text: `${kind} — encaissement ${paiementCode}`,
      });
      if (outcome === 'downloaded') {
        toast.success('Fichier téléchargé — joins-le à ton message.');
      }
    } catch (e) {
      toast.error(`Partage échoué : ${e instanceof Error ? e.message : 'inconnu'}`);
    } finally {
      setSharing(false);
    }
  }

  if (isLoading) {
    return (
      <div className="border-border bg-muted/30 flex size-24 items-center justify-center rounded border">
        <Loader2 className="text-muted-foreground size-5 animate-spin" />
      </div>
    );
  }
  if (!signedUrl) {
    return (
      <div className="border-border bg-muted/30 flex size-24 flex-col items-center justify-center rounded border p-1 text-center">
        <AlertCircle className="text-muted-foreground size-4" />
        <p className="text-muted-foreground text-[10px]">URL expirée</p>
      </div>
    );
  }
  return (
    <div className="w-24 space-y-1">
      <a
        href={signedUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={`${kind} — ${filename}`}
        className="border-border hover:border-primary group relative block size-24 overflow-hidden rounded border transition-colors"
      >
        {isImage ? (
          <img src={signedUrl} alt={filename} className="size-full object-cover" loading="lazy" />
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-1 p-1 text-center">
            <ImageIcon className="text-muted-foreground size-7" />
            <span className="text-muted-foreground truncate text-[10px]">{filename}</span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
          {kind}
        </div>
      </a>
      {/* Partage du fichier lui-même (pas du lien signé, énorme et périssable). */}
      <button
        type="button"
        onClick={() => void handleShare()}
        disabled={sharing}
        className="border-input hover:bg-accent text-foreground inline-flex h-7 w-full items-center justify-center gap-1 rounded-md border text-[11px] font-medium transition-colors disabled:opacity-50"
      >
        {sharing ? <Loader2 className="size-3 animate-spin" /> : <Share2 className="size-3" />}
        Partager
      </button>
    </div>
  );
}

// ─── History timeline ────────────────────────────────────
function HistoryTimeline({ paiement }: { paiement: Paiement }) {
  interface Event {
    at: Date;
    title: string;
    who: string | null;
    icon: typeof Send;
    iconColor: string;
    details?: string;
  }

  const events: Event[] = [
    {
      at: paiement.createdAt,
      icon: Send,
      iconColor: 'text-blue-600 dark:text-blue-400',
      title: 'Encaissement créé',
      who: shortEmail(paiement.createdByEmail),
    },
  ];

  if (paiement.argentRecuAt) {
    events.push({
      at: paiement.argentRecuAt,
      icon: CheckCircle2,
      iconColor: 'text-green-600 dark:text-green-400',
      title: paiement.argentRecuAuto ? 'Argent reçu (auto)' : 'Argent reçu (manuel)',
      who: paiement.argentRecuParEmail ? shortEmail(paiement.argentRecuParEmail) : null,
    });
  }
  if (paiement.confirmeAt) {
    events.push({
      at: paiement.confirmeAt,
      icon: ClipboardCheck,
      iconColor: 'text-green-700 dark:text-green-300',
      title: `Confirmé Sage${paiement.refCompta ? ` (${paiement.refCompta})` : ''}`,
      who: paiement.confirmeParEmail ? shortEmail(paiement.confirmeParEmail) : null,
    });
  }
  if (paiement.rejetDate) {
    events.push({
      at: paiement.rejetDate,
      icon: XCircle,
      iconColor: 'text-red-600 dark:text-red-400',
      title: 'Rejeté',
      who: paiement.rejetParEmail ? shortEmail(paiement.rejetParEmail) : null,
      details: paiement.rejetMotif ?? undefined,
    });
  }
  if (paiement.annuleeAt) {
    events.push({
      at: paiement.annuleeAt,
      icon: XCircle,
      iconColor: 'text-gray-500 dark:text-gray-400',
      title: 'Encaissement annulé',
      who: paiement.annuleeParEmail ? shortEmail(paiement.annuleeParEmail) : null,
      details: paiement.annuleeMotif ?? undefined,
    });
  }

  events.sort((a, b) => a.at.getTime() - b.at.getTime());

  if (events.length === 0) return null;

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <p className="text-foreground font-medium">Historique</p>
        <ol className="space-y-3">
          {events.map((e, i) => {
            const Icon = e.icon;
            return (
              <li key={i} className="flex items-start gap-3">
                <div className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-full">
                  <Icon className={cn('size-4', e.iconColor)} />
                </div>
                <div className="flex-1">
                  <p className="text-foreground text-sm font-medium">{e.title}</p>
                  <p className="text-muted-foreground text-xs">
                    {new Intl.DateTimeFormat('fr-FR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }).format(e.at)}
                    {e.who && ` · par ${e.who}`}
                  </p>
                  {e.details && (
                    <p className="text-muted-foreground mt-0.5 text-xs italic">{e.details}</p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}

function shortEmail(email: string): string {
  return email.split('@')[0] ?? email;
}

// ─── Anomaly inline (affichage simple + acquittement) ────
function AnomalyInline({ anomaly }: { anomaly: PaiementAnomaly }) {
  const ackMutation = useAcknowledgePaiementAnomaly();
  const colors = SEVERITY_COLORS[anomaly.severity];

  return (
    <div
      className={cn(
        'rounded-md border p-3 transition-opacity',
        anomaly.acknowledgedAt && 'opacity-60',
      )}
      style={{ borderColor: colors.bg }}
    >
      <div className="flex items-start gap-2">
        <span
          className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
          style={{ color: colors.fg, backgroundColor: colors.bg }}
        >
          {ANOMALY_TYPE_LABELS[anomaly.type] ?? anomaly.type}
        </span>
        <p className="text-foreground flex-1 text-sm">{anomaly.message}</p>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">
          {anomaly.acknowledgedAt
            ? `Traitée par ${anomaly.acknowledgedByEmail?.split('@')[0] ?? 'inconnu'}`
            : new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              }).format(anomaly.detectedAt)}
        </span>
        {!anomaly.acknowledgedAt && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            disabled={ackMutation.isPending}
            onClick={() => {
              ackMutation.mutate(anomaly.id, {
                onSuccess: () => toast.success('Anomalie traitée'),
                onError: (e) =>
                  toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`),
              });
            }}
          >
            <CheckCircle2 className="size-3" />
            Marquer traité
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Actions bar ─────────────────────────────────────────
type ActionMode =
  | 'confirm'
  | 'reject'
  | 'markRecu'
  | 'uploadBordereau'
  | 'annuler'
  | 'delete'
  | null;

function ActionsBar({
  paiement,
  actions,
  canAnnuler,
  memeBanque,
  onDeleted,
}: {
  paiement: Paiement;
  actions: ReturnType<typeof computeActions>;
  canAnnuler: boolean;
  memeBanque: boolean;
  onDeleted: () => void;
}) {
  const [mode, setMode] = useState<ActionMode>(null);

  // Sprint 4 patch : chèque + cash "à déposer" → bouton Upload bordereau visible
  // V1 fidèle : bordereau toujours requis pour chèque (preuve dépôt) ET cash
  // (preuve versement bancaire d'espèces).
  const isChequeADeposer =
    (paiement.mode === 'cheque' || paiement.mode === 'cash') &&
    !paiement.bordereauUrl &&
    actions.canUploadBordereau;

  const hasAny =
    actions.canConfirmSage ||
    actions.canReject ||
    actions.canMarkArgentRecu ||
    isChequeADeposer ||
    canAnnuler ||
    actions.canDelete;

  if (!hasAny) return null;

  return (
    <>
      <div className="bg-background border-border fixed inset-x-0 bottom-16 z-40 border-t p-3 pb-[env(safe-area-inset-bottom,0)] sm:relative sm:bottom-0 sm:rounded-lg sm:border sm:p-4">
        <div className="mx-auto flex max-w-3xl flex-wrap gap-2">
          {actions.canConfirmSage && (
            <Button
              type="button"
              onClick={() => setMode('confirm')}
              className="flex-1 gap-2 bg-green-600 hover:bg-green-700 sm:flex-initial"
            >
              <ClipboardCheck className="size-4" />
              Confirmer Sage
            </Button>
          )}
          {actions.canReject && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => setMode('reject')}
              className="flex-1 gap-2 sm:flex-initial"
            >
              <XCircle className="size-4" />
              Rejeter
            </Button>
          )}
          {actions.canMarkArgentRecu && (
            <Button
              type="button"
              onClick={() => setMode('markRecu')}
              variant="outline"
              className="flex-1 gap-2 sm:flex-initial"
            >
              <CheckCircle2 className="size-4" />
              Argent reçu
            </Button>
          )}
          {isChequeADeposer && (
            <Button
              type="button"
              onClick={() => setMode('uploadBordereau')}
              className="flex-1 gap-2 bg-orange-600 hover:bg-orange-700 sm:flex-initial"
            >
              <Upload className="size-4" />
              Upload bordereau
            </Button>
          )}
          {canAnnuler && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setMode('annuler')}
              className="gap-2"
            >
              <XCircle className="size-4" />
              Annuler
            </Button>
          )}
          {actions.canDelete && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => setMode('delete')}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </div>
      </div>

      {mode && (
        <ActionSheet
          paiement={paiement}
          mode={mode}
          memeBanque={memeBanque}
          onClose={() => setMode(null)}
          onSuccess={() => setMode(null)}
          onDeleted={() => {
            setMode(null);
            onDeleted();
          }}
        />
      )}
    </>
  );
}

// ─── Action sheets ───────────────────────────────────────
function ActionSheet({
  paiement,
  mode,
  memeBanque,
  onClose,
  onSuccess,
  onDeleted,
}: {
  paiement: Paiement;
  mode: Exclude<ActionMode, null>;
  memeBanque: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onDeleted: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-background w-full max-w-md rounded-t-xl border p-4 sm:rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {mode === 'confirm' && <ConfirmForm paiement={paiement} onSuccess={onSuccess} />}
        {mode === 'reject' && <RejectForm paiement={paiement} onSuccess={onSuccess} />}
        {mode === 'markRecu' && <MarkRecuForm paiement={paiement} onSuccess={onSuccess} />}
        {mode === 'uploadBordereau' && (
          <UploadBordereauForm paiement={paiement} memeBanque={memeBanque} onSuccess={onSuccess} />
        )}
        {mode === 'annuler' && <AnnulerForm paiement={paiement} onSuccess={onSuccess} />}
        {mode === 'delete' && <DeleteForm paiement={paiement} onDeleted={onDeleted} />}
        <div className="mt-3 flex justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConfirmForm({ paiement, onSuccess }: { paiement: Paiement; onSuccess: () => void }) {
  const confirm = useConfirmPaiement();
  const form = useForm<{ refCompta: string }>({
    resolver: zodResolver(PaiementConfirmSchema),
    defaultValues: { refCompta: '' },
  });
  const onSubmit: SubmitHandler<{ refCompta: string }> = async (values) => {
    try {
      await confirm.mutateAsync({ id: paiement.id, refCompta: values.refCompta });
      toast.success('Confirmé Sage');
      onSuccess();
    } catch (e) {
      toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
    }
  };
  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
      <p className="font-medium">Confirmer Sage</p>
      <div>
        <Label htmlFor="refCompta">Référence Sage</Label>
        <Input id="refCompta" {...form.register('refCompta')} placeholder="N° pièce Sage" />
        {form.formState.errors.refCompta && (
          <p className="mt-1 text-xs text-red-600">{form.formState.errors.refCompta.message}</p>
        )}
      </div>
      <Button type="submit" disabled={confirm.isPending} className="w-full gap-2">
        {confirm.isPending && <Loader2 className="size-4 animate-spin" />}
        Confirmer
      </Button>
    </form>
  );
}

function RejectForm({ paiement, onSuccess }: { paiement: Paiement; onSuccess: () => void }) {
  const reject = useRejectPaiement();
  const form = useForm<{ motif: string }>({
    resolver: zodResolver(PaiementRejectSchema),
    defaultValues: { motif: '' },
  });
  const onSubmit: SubmitHandler<{ motif: string }> = async (values) => {
    try {
      await reject.mutateAsync({ id: paiement.id, motif: values.motif });
      toast.success('Encaissement rejeté');
      onSuccess();
    } catch (e) {
      toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
    }
  };
  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
      <p className="font-medium">Rejeter l&apos;encaissement</p>
      <div>
        <Label htmlFor="motif">Motif</Label>
        <textarea
          id="motif"
          {...form.register('motif')}
          rows={3}
          className="border-input bg-background w-full rounded-md border p-2 text-sm"
          placeholder="Pourquoi rejeter cet encaissement ?"
        />
        {form.formState.errors.motif && (
          <p className="mt-1 text-xs text-red-600">{form.formState.errors.motif.message}</p>
        )}
      </div>
      <Button type="submit" variant="destructive" disabled={reject.isPending} className="w-full">
        {reject.isPending && <Loader2 className="size-4 animate-spin" />}
        Rejeter
      </Button>
    </form>
  );
}

function MarkRecuForm({ paiement, onSuccess }: { paiement: Paiement; onSuccess: () => void }) {
  const mark = useMarkArgentRecu();
  return (
    <div className="space-y-3">
      <p className="font-medium">Marquer argent reçu</p>
      <p className="text-muted-foreground text-sm">
        L&apos;argent de {formatMRU(paiement.montant)} a-t-il bien été reçu en caisse / sur le
        compte bancaire ?
      </p>
      <Button
        type="button"
        className="w-full"
        disabled={mark.isPending}
        onClick={() => {
          void (async () => {
            try {
              await mark.mutateAsync({ id: paiement.id });
              toast.success('Argent marqué reçu');
              onSuccess();
            } catch (e) {
              toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
            }
          })();
        }}
      >
        {mark.isPending && <Loader2 className="size-4 animate-spin" />}
        Confirmer réception
      </Button>
    </div>
  );
}

// ─── Bandeau "Rejeté par comptable" + modal édition (V1 fidèle) ───
function PaiementRejeteBanner({ paiement, canEdit }: { paiement: Paiement; canEdit: boolean }) {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <>
      <Card className="border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950/30">
        <CardContent className="space-y-3 py-3">
          <div className="flex items-start gap-3">
            <XCircle className="size-5 shrink-0 text-red-600 dark:text-red-400" />
            <div className="flex-1 space-y-1">
              <p className="text-sm font-medium text-red-900 dark:text-red-100">
                Encaissement rejeté par le comptable
              </p>
              <p className="text-sm text-red-800 dark:text-red-200">
                <span className="font-medium">Motif :</span> {paiement.rejetMotif}
              </p>
              {paiement.rejetParEmail && paiement.rejetDate && (
                <p className="text-xs text-red-700 dark:text-red-300">
                  Par {paiement.rejetParEmail.split('@')[0]} le{' '}
                  {new Intl.DateTimeFormat('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  }).format(paiement.rejetDate)}
                </p>
              )}
            </div>
          </div>
          {canEdit ? (
            <Button
              type="button"
              onClick={() => setModalOpen(true)}
              className="w-full gap-2 bg-red-600 hover:bg-red-700 sm:w-auto"
            >
              Modifier et re-soumettre
            </Button>
          ) : (
            <p className="text-xs italic text-red-700 dark:text-red-300">
              (Modification réservée au créateur et DAF.)
            </p>
          )}
        </CardContent>
      </Card>

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-background max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-xl border p-4 sm:rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <EditPaiementForm paiement={paiement} onSuccess={() => setModalOpen(false)} />
            <div className="mt-3 flex justify-end">
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EditPaiementForm({ paiement, onSuccess }: { paiement: Paiement; onSuccess: () => void }) {
  const update = useUpdatePaiement();
  const [montant, setMontant] = useState<number | null>(paiement.montant);
  const [clientNom, setClientNom] = useState(paiement.clientNom);
  const [numCheque, setNumCheque] = useState(paiement.numCheque ?? '');
  const [refTransaction, setRefTransaction] = useState(paiement.refTransaction ?? '');
  const [commentaire, setCommentaire] = useState(paiement.commentaire ?? '');

  const onSubmit = async () => {
    if (montant === null || !isFinite(montant) || montant <= 0) {
      toast.error('Montant invalide');
      return;
    }
    if (!clientNom.trim()) {
      toast.error('Nom client requis');
      return;
    }
    if (paiement.mode === 'cheque' && !numCheque.trim()) {
      toast.error('N° de chèque requis');
      return;
    }
    try {
      await update.mutateAsync({
        id: paiement.id,
        patch: {
          montant,
          clientNom: clientNom.trim(),
          numCheque: paiement.mode === 'cheque' ? numCheque.trim() : null,
          refTransaction: paiement.mode === 'wallet' ? refTransaction.trim() : null,
          commentaire: commentaire.trim() || null,
        },
      });
      toast.success('Encaissement modifié + re-soumis au comptable');
      onSuccess();
    } catch (e) {
      toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="font-medium">Modifier l&apos;encaissement</p>
        <p className="text-muted-foreground text-xs">
          Corrige les infos selon les remarques du comptable. Au save, le motif sera effacé et
          l&apos;encaissement repassera en attente.
        </p>
      </div>
      <div>
        <Label htmlFor="edit-client">Client</Label>
        <Input
          id="edit-client"
          value={clientNom}
          onChange={(e) => setClientNom(e.target.value)}
          placeholder="Nom client"
        />
      </div>
      <div>
        <Label htmlFor="edit-montant">Montant (MRU)</Label>
        <MoneyInput
          id="edit-montant"
          value={montant}
          onChange={(v) => setMontant(v)}
          placeholder="100 000"
        />
      </div>
      {paiement.mode === 'cheque' && (
        <div>
          <Label htmlFor="edit-cheque">N° chèque</Label>
          <Input
            id="edit-cheque"
            value={numCheque}
            onChange={(e) => setNumCheque(e.target.value)}
            placeholder="001234"
          />
        </div>
      )}
      {paiement.mode === 'wallet' && (
        <div>
          <Label htmlFor="edit-ref">Référence transaction</Label>
          <Input
            id="edit-ref"
            value={refTransaction}
            onChange={(e) => setRefTransaction(e.target.value)}
            placeholder="01260513..."
          />
        </div>
      )}
      <div>
        <Label htmlFor="edit-commentaire">Commentaire (optionnel)</Label>
        <Input
          id="edit-commentaire"
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
          placeholder="…"
        />
      </div>
      <Button
        type="button"
        className="w-full"
        disabled={update.isPending}
        onClick={() => {
          void onSubmit();
        }}
      >
        {update.isPending && <Loader2 className="size-4 animate-spin" />}
        Re-soumettre au comptable
      </Button>
    </div>
  );
}

// ─── Bandeau "À déposer" (chèque ou cash, visible top of page, V1 fidèle) ───
function ChequeADeposerBanner({
  paiement,
  canUpload,
  memeBanque,
}: {
  paiement: Paiement;
  canUpload: boolean;
  memeBanque: boolean;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const isCash = paiement.mode === 'cash';
  const isMemeBanque = memeBanque;

  const title = isCash ? 'Espèces à verser à la banque' : 'Chèque à déposer en banque';
  const subtitle = isCash
    ? 'Verse les espèces à la banque puis upload le bordereau de versement tamponné. Une fois uploadé → l\'encaissement passe en "À comptabiliser".'
    : isMemeBanque
      ? 'Dépose le chèque à la banque puis upload le bordereau de remise tamponné. Même banque → encaissement du jour automatique.'
      : 'Dépose le chèque à la banque puis upload le bordereau de remise tamponné. Banques différentes → attente compensation interbancaire (le comptable confirme la réception).';

  return (
    <>
      <Card className="border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-950/30">
        <CardContent className="space-y-3 py-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="size-5 shrink-0 text-orange-600 dark:text-orange-400" />
            <div className="flex-1 space-y-0.5">
              <p className="text-sm font-medium text-orange-900 dark:text-orange-100">{title}</p>
              <p className="text-xs text-orange-700 dark:text-orange-300">{subtitle}</p>
            </div>
          </div>
          {canUpload ? (
            <Button
              type="button"
              onClick={() => setModalOpen(true)}
              className="w-full gap-2 bg-orange-600 hover:bg-orange-700 sm:w-auto"
            >
              <Upload className="size-4" />
              {isCash ? 'Upload bordereau de versement' : 'Upload bordereau de remise'}
            </Button>
          ) : (
            <p className="text-xs italic text-orange-700 dark:text-orange-300">
              (Upload réservé à Fatimetou et DAF.)
            </p>
          )}
        </CardContent>
      </Card>

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-background w-full max-w-md rounded-t-xl border p-4 sm:rounded-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <UploadBordereauForm
              paiement={paiement}
              memeBanque={memeBanque}
              onSuccess={() => setModalOpen(false)}
            />
            <div className="mt-3 flex justify-end">
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function UploadBordereauForm({
  paiement,
  memeBanque,
  onSuccess,
}: {
  paiement: Paiement;
  memeBanque: boolean;
  onSuccess: () => void;
}) {
  const upload = useUploadBordereauPaiement();
  const [file, setFile] = useState<File | null>(null);

  const isCash = paiement.mode === 'cash';
  const isMemeBanque = memeBanque;

  const onSubmit = async () => {
    if (!file) {
      toast.error('Sélectionne un fichier (photo ou PDF du bordereau)');
      return;
    }
    try {
      await upload.mutateAsync({
        paiementId: paiement.id,
        tenantId: paiement.tenantId,
        // Pour cash : argent_recu déjà true → pas de logique "même banque", on passe true
        // pour avoir le passage à "à comptabiliser" (le bordereau sert juste à prouver
        // le versement physique, l'argent est déjà encaissé du jour)
        isMemeBanque: isCash ? true : isMemeBanque,
        mode: paiement.mode,
        file,
      });
      if (isCash) {
        toast.success(`Bordereau de versement enregistré — passe en "À comptabiliser".`);
      } else if (isMemeBanque) {
        toast.success(`Bordereau enregistré — encaissement du jour confirmé (même banque).`);
      } else {
        toast.success(`Bordereau enregistré — en attente compensation interbancaire.`);
      }
      onSuccess();
    } catch (e) {
      toast.error(`Erreur upload : ${e instanceof Error ? e.message : 'inconnu'}`);
    }
  };

  return (
    <div className="space-y-3">
      <p className="font-medium">
        {isCash ? 'Upload bordereau de versement' : 'Upload bordereau de remise'}
      </p>
      <p className="text-muted-foreground text-sm">
        {isCash
          ? `Photo ou PDF du bordereau de versement d'espèces (${formatMRU(paiement.montant)}).`
          : `Photo ou PDF du bordereau (la banque a tamponné après le dépôt du chèque ${paiement.numCheque ?? ''} pour ${formatMRU(paiement.montant)}).`}
      </p>
      {isCash ? (
        <p className="text-xs text-green-700 dark:text-green-300">
          ℹ️ Cash déjà encaissé du jour → après upload du bordereau, passe à &quot;À
          comptabiliser&quot;.
        </p>
      ) : isMemeBanque ? (
        <p className="text-xs text-green-700 dark:text-green-300">
          ℹ️ Banque émettrice = banque d&apos;encaissement → l&apos;encaissement passe en caisse du
          jour automatiquement.
        </p>
      ) : (
        <p className="text-xs text-orange-700 dark:text-orange-300">
          ℹ️ Banques différentes → après upload bordereau, attente compensation interbancaire (2-3
          jours). Le comptable marquera &quot;Argent reçu&quot; quand la banque confirme.
        </p>
      )}
      <div className="flex flex-col gap-2">
        <Input
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file && (
          <p className="text-muted-foreground text-xs">
            {file.name} ({Math.round(file.size / 1024)} KB)
          </p>
        )}
      </div>
      <Button
        type="button"
        className="w-full"
        disabled={!file || upload.isPending}
        onClick={() => {
          void onSubmit();
        }}
      >
        {upload.isPending && <Loader2 className="size-4 animate-spin" />}
        Uploader le bordereau
      </Button>
    </div>
  );
}

function AnnulerForm({ paiement, onSuccess }: { paiement: Paiement; onSuccess: () => void }) {
  const annuler = useAnnulerPaiement();
  const form = useForm<{ motif: string }>({
    resolver: zodResolver(PaiementRejectSchema),
    defaultValues: { motif: '' },
  });
  const onSubmit: SubmitHandler<{ motif: string }> = async (values) => {
    try {
      await annuler.mutateAsync({ id: paiement.id, motif: values.motif });
      toast.success('Encaissement annulé');
      onSuccess();
    } catch (e) {
      toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
    }
  };
  return (
    <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-3">
      <p className="font-medium">Annuler l&apos;encaissement</p>
      <p className="text-muted-foreground text-sm">
        Cette action est terminale. L&apos;encaissement ne sera plus comptabilisable.
      </p>
      <div>
        <Label htmlFor="motif">Motif</Label>
        <textarea
          id="motif"
          {...form.register('motif')}
          rows={3}
          className="border-input bg-background w-full rounded-md border p-2 text-sm"
          placeholder="Pourquoi annuler ?"
        />
        {form.formState.errors.motif && (
          <p className="mt-1 text-xs text-red-600">{form.formState.errors.motif.message}</p>
        )}
      </div>
      <Button type="submit" variant="destructive" disabled={annuler.isPending} className="w-full">
        {annuler.isPending && <Loader2 className="size-4 animate-spin" />}
        Confirmer annulation
      </Button>
    </form>
  );
}

function DeleteForm({ paiement, onDeleted }: { paiement: Paiement; onDeleted: () => void }) {
  const del = useDeletePaiement();
  return (
    <div className="space-y-3">
      <p className="font-medium text-red-600">Supprimer l&apos;encaissement</p>
      <p className="text-muted-foreground text-sm">
        Action irréversible. Confirmer pour supprimer {paiement.code} ({formatMRU(paiement.montant)}
        ).
      </p>
      <Button
        type="button"
        variant="destructive"
        className="w-full"
        disabled={del.isPending}
        onClick={() => {
          void (async () => {
            try {
              await del.mutateAsync(paiement.id);
              toast.success('Encaissement supprimé');
              onDeleted();
            } catch (e) {
              toast.error(`Erreur : ${e instanceof Error ? e.message : 'inconnu'}`);
            }
          })();
        }}
      >
        {del.isPending && <Loader2 className="size-4 animate-spin" />}
        Confirmer suppression
      </Button>
    </div>
  );
}
